import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { ADMIN_ROLE, DATA_TEAM_ROLE } from "consts";
import DateInputGroup from "components/DateInputGroup";
import FormGrid from "components/FormGrid";
import TextArea from "components/TextArea";
import FormGroup from "components/FormGroup";
import InputGroup from "components/InputGroup";
import SelectEntityType from "components/SelectEntityType";
import SelectCountryCode from "components/SelectCountryCode";
import IntegerInputGroup from "components/IntegerInputGroup";
import SelectEntityMaster from "components/SelectEntityMaster";
import PermissionValidator from "components/PermissionValidator";
import SelectNorthAmericanState from "components/SelectNorthAmericanState";
import getEntityMasterByIdService from "services/entityMaster/getEntityMasterByIdService";

const PRISM_ENTITY_HELPER =
  "Create or edit entities in PRISM, then select an existing entity here.";

function mapEntityToManagerId(entity) {
  if (!entity) {
    return "";
  }
  return {
    entity_id: get(entity, "entity_id"),
    long_name: get(entity, "long_name"),
    editorial_name: get(entity, "editorial_name"),
    editorial_description: get(entity, "editorial_description"),
    address: get(entity, "address"),
    city: get(entity, "city"),
    postal_code: get(entity, "postal_code"),
    phone: get(entity, "phone"),
    website: get(entity, "website"),
    year_founded: get(entity, "year_founded"),
    search_term: get(entity, "search_term"),
    entity_type: get(entity, "entity_type.entity_code", get(entity, "entity_type")),
    parent_entity: get(entity, "parent_entity.entity_id", get(entity, "parent_entity")),
    country_code: get(entity, "country_code.country_code", get(entity, "country_code")),
    state: get(entity, "state.state_code", get(entity, "state")),
    date_start: get(entity, "date_start"),
  };
}

function HYManagerFormFields({
  getValue,
  setValue,
  getError,
  isSubmitting,
  isNew,
}) {
  const onSelectManagerEntity = async (entityId) => {
    if (!entityId) {
      setValue("manager_id", "");
      return;
    }
    const entity = await getEntityMasterByIdService({ id: entityId });
    setValue("manager_id", mapEntityToManagerId(entity));
  };

  return (
    <PermissionValidator
      allowedFor={[ADMIN_ROLE, DATA_TEAM_ROLE]}
      shouldHide={false}
    >
      {({ isAllowed }) => (
        <React.Fragment>
          <FormGrid>
            <FormGrid.Item>
              <FormGroup
                label="Manager ID"
                required
                error={getError("manager_id.entity_id") || getError("manager_id")}
                disabled={isSubmitting || !isNew}
                helperText={isNew ? PRISM_ENTITY_HELPER : null}
              >
                <SelectEntityMaster
                  onSelect={onSelectManagerEntity}
                  value={getValue("manager_id.entity_id")}
                  error={
                    getError("manager_id.entity_id") || getError("manager_id")
                  }
                  readOnly={!isAllowed}
                  disabled={isSubmitting || !isNew}
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Full name" disabled>
                <InputGroup
                  value={getValue("manager_id.long_name")}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Editorial name" disabled>
                <InputGroup
                  value={getValue("manager_id.editorial_name")}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Editorial description" disabled>
                <InputGroup
                  value={getValue("manager_id.editorial_description")}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Entity type" disabled>
                <SelectEntityType
                  onSelect={() => {}}
                  value={getValue("manager_id.entity_type")}
                  disabled
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Parent Entity" disabled>
                <SelectEntityMaster
                  onSelect={() => {}}
                  value={getValue("manager_id.parent_entity")}
                  disabled
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Phone" disabled>
                <InputGroup
                  value={getValue("manager_id.phone")}
                  maxLength={15}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Website" disabled>
                <InputGroup
                  value={getValue("manager_id.website")}
                  maxLength={100}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Year founded" disabled>
                <IntegerInputGroup
                  value={getValue("manager_id.year_founded")}
                  disabled
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Search term" disabled>
                <InputGroup
                  value={getValue("manager_id.search_term")}
                  maxLength={100}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Effective date" disabled>
                <DateInputGroup
                  value={getValue("manager_id.date_start")}
                  disabled
                />
              </FormGroup>
            </FormGrid.Item>
          </FormGrid>
          <FormGrid>
            <FormGrid.Item>
              <FormGroup label="Address" disabled>
                <InputGroup
                  value={getValue("manager_id.address")}
                  maxLength={100}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="City" disabled>
                <InputGroup
                  value={getValue("manager_id.city")}
                  maxLength={100}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="State" disabled>
                <SelectNorthAmericanState
                  onSelect={() => {}}
                  value={getValue("manager_id.state")}
                  disabled
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Postal code" disabled>
                <InputGroup
                  value={getValue("manager_id.postal_code")}
                  maxLength={9}
                  disabled
                  readOnly
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Country" disabled>
                <SelectCountryCode
                  onSelect={() => {}}
                  value={getValue("manager_id.country_code")}
                  disabled
                />
              </FormGroup>
            </FormGrid.Item>
          </FormGrid>
          <FormGrid>
            <FormGrid.Item>
              <FormGroup label="Public Note" error={getError("public_note")}>
                <TextArea
                  intent={getError("public_note") ? "danger" : null}
                  value={getValue("public_note")}
                  onChange={(value) => setValue("public_note", value)}
                  readOnly={!isAllowed}
                  fullWidth
                />
              </FormGroup>
            </FormGrid.Item>
            <FormGrid.Item>
              <FormGroup label="Private Note" error={getError("private_note")}>
                <TextArea
                  intent={getError("private_note") ? "danger" : null}
                  value={getValue("private_note")}
                  onChange={(value) => setValue("private_note", value)}
                  readOnly={!isAllowed}
                  fullWidth
                />
              </FormGroup>
            </FormGrid.Item>
          </FormGrid>
        </React.Fragment>
      )}
    </PermissionValidator>
  );
}

HYManagerFormFields.propTypes = {
  getValue: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getError: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isNew: PropTypes.bool,
};

HYManagerFormFields.defaultProps = {
  isSubmitting: false,
  isNew: true,
};

export default HYManagerFormFields;
